import React, { useEffect, useState } from "react"
import { ShieldCheck } from "lucide-react"

import { Button, Input } from "@aix/ui"

import { useAuth } from "../../lib/auth-context"
import { isFactorNotFoundError, verifyTotpCode } from "../../lib/mfaVerify"
import { supabase } from "../../lib/supabase"

/**
 * Forced-MFA gate UI. Rendered by ProtectedRoute (and the CLI-auth page) only when
 * the auth context reports `mfaStatus` of "enroll" or "challenge". On a successful
 * verify it calls `recheckMfa()`, which flips the context to "ok" and unmounts this.
 *
 * - "enroll":    user has no TOTP factor → show QR + secret, enroll, verify.
 * - "challenge": user has a verified factor but an aal1 session → step up with a code.
 */
export function MfaGate({
  mode,
  onVerified,
}: {
  mode?: "enroll" | "challenge"
  onVerified?: () => Promise<void>
} = {}) {
  const { mfaStatus: sessionMfaStatus, recheckMfa, signOut } = useAuth()
  // Sensitive actions can require aal2 even when the workspace accepts a passkey session.
  const mfaStatus = mode ?? sessionMfaStatus
  const [factorId, setFactorId] = useState<string | null>(null)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [initializing, setInitializing] = useState(mfaStatus === "enroll")
  const [enrollAttempt, setEnrollAttempt] = useState(0)
  const [notice, setNotice] = useState("")

  // For "enroll": clear any stale unverified factor, then create a fresh one so we
  // get a QR + secret. Reusing an old factor isn't possible (QR is create-time only).
  useEffect(() => {
    if (mfaStatus !== "enroll") return
    let cancelled = false
    async function startEnrollment() {
      setInitializing(true)
      setError("")
      try {
        const { data: list } = await supabase.auth.mfa.listFactors()
        const stale = (list?.all ?? []).filter((f) => f.factor_type === "totp" && f.status !== "verified")
        for (const f of stale) {
          await supabase.auth.mfa.unenroll({ factorId: f.id })
        }
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: "totp",
          friendlyName: "AIX Command Center",
        })
        if (error || !data) throw error ?? new Error("Enrollment returned no data")
        if (cancelled) return
        setFactorId(data.id)
        setQrCode(data.totp.qr_code)
        setSecret(data.totp.secret)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to start MFA enrollment")
      } finally {
        if (!cancelled) setInitializing(false)
      }
    }
    void startEnrollment()
    return () => {
      cancelled = true
    }
  }, [mfaStatus, enrollAttempt])

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError("")
    try {
      // "enroll" verifies the new factor; "challenge" tries every verified factor.
      await verifyTotpCode(code.trim(), factorId)
      // Stepped up to aal2 — refresh the session and re-evaluate the gate.
      await supabase.auth.getSession()
      await recheckMfa()
      await onVerified?.()
    } catch (e) {
      if (mfaStatus === "enroll" && isFactorNotFoundError(e)) {
        // Another tab or a remount replaced this QR's factor; start over with a fresh one.
        setCode("")
        setNotice("That QR code expired. Scan this new one into 1Password, replacing the old code.")
        setEnrollAttempt((n) => n + 1)
        return
      }
      setError(e instanceof Error ? e.message : "Verification failed")
    } finally {
      setBusy(false)
    }
  }

  const isEnroll = mfaStatus === "enroll"

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card p-8">
        <div className="space-y-2 text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-primary" />
          <h1 className="text-xl font-bold text-foreground">
            {isEnroll ? "Set up two-factor authentication" : "Two-factor verification"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEnroll
              ? "MFA is required for this workspace. Scan the QR code with 1Password or another authenticator, then enter the 6-digit code."
              : "Enter the 6-digit code from 1Password or your authenticator to continue."}
          </p>
        </div>

        {isEnroll &&
          (initializing ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : (
            qrCode && (
              <div className="space-y-3 text-center">
                <img src={qrCode} alt="MFA QR code" className="mx-auto h-44 w-44 rounded bg-white p-2" />
                <p className="text-xs text-muted-foreground">
                  In 1Password, open your login for agent.aixlabs.co and choose Scan QR Code.
                </p>
                {secret && (
                  <details className="text-xs text-muted-foreground">
                    <summary className="cursor-pointer">Can&apos;t scan? Show setup key</summary>
                    <p className="mt-2 break-all font-mono text-foreground">{secret}</p>
                  </details>
                )}
              </div>
            )
          ))}

        <form onSubmit={handleVerify} className="space-y-4">
          <label htmlFor="mfa-one-time-code" className="sr-only">
            One-time code
          </label>
          <Input
            id="mfa-one-time-code"
            name="one-time-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="text-center text-lg tracking-widest"
            autoFocus
          />

          {notice && <div className="rounded-md bg-secondary p-3 text-sm text-foreground">{notice}</div>}
          {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

          <Button
            type="submit"
            className="w-full"
            loading={busy}
            disabled={code.length !== 6 || (isEnroll && initializing)}
          >
            {isEnroll ? "Enroll & continue" : "Verify"}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => void signOut()}
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}

export function MfaCheckError() {
  const { recheckMfa, signOut } = useAuth()
  const [retrying, setRetrying] = useState(false)

  async function retry() {
    setRetrying(true)
    try {
      await recheckMfa()
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-8 text-center">
        <ShieldCheck className="mx-auto h-10 w-10 text-primary" />
        <h1 className="text-xl font-bold text-foreground">Unable to check two-factor authentication</h1>
        <p className="text-sm text-muted-foreground">Check your connection and try again.</p>
        <Button className="w-full" loading={retrying} onClick={() => void retry()}>
          Retry
        </Button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
